import { Fragment, useState } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { CommunityListings } from './CommunityListings';
import { useSnackbar } from 'notistack';
import { usePermissions } from '@/hooks/usePermissions';
import * as Yup from 'yup';
import { communityValidationSchema } from '@/utils/validations';
import { useCreateCommunityMutation } from '@/services/CommunityManagementService';
import { ErrorApiResponse } from '@/types/api.types';
import {
  ICommunityData,
  CommunityStatus,
} from '@/types/community-management.types';
import { CommunityModal } from './CommunityModal';

const CommunityManagement = () => {
  const { currentLayout } = useLayout();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<ICommunityData>>({
    moderator_id: '',
    name: '',
    description: '',
    status: CommunityStatus.GLOBAL,
    tags: [],
    max_users: undefined,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { enqueueSnackbar } = useSnackbar();
  const [createCommunity, { isLoading: isCreating }] =
    useCreateCommunityMutation();
  const { hasPermission } = usePermissions();

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
    setFormData({
      moderator_id: '',
      name: '',
      description: '',
      status: CommunityStatus.GLOBAL,
      tags: [],
      max_users: undefined,
    });
    setErrors({});
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setFormData({});
    setErrors({});
  };

  const handleSubmit = async (data: Partial<ICommunityData>) => {
    try {
      await communityValidationSchema.validate(data, { abortEarly: false });

      const payload = {
        ...data,
      };

      await createCommunity(payload).unwrap();
      enqueueSnackbar('Community created successfully', { variant: 'success' });
      handleCloseCreateModal();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar(
          `Please fix the form errors (${err.inner.length} errors occurred)`,
          { variant: 'error' }
        );
      } else {
        const errorMessage =
          (err as ErrorApiResponse).data?.message ||
          'Failed to create community';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="community-management-heading" />
              <ToolbarDescription>
                You can manage your communities here.
              </ToolbarDescription>
            </ToolbarHeading>
            {hasPermission('community_management_create') && (
              <ToolbarActions>
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={handleOpenCreateModal}
                  aria-label="Add new community"
                  title="Add new community"
                >
                  Add New Community
                </button>
              </ToolbarActions>
            )}
          </Toolbar>
        </Container>
      )}
      <Container>
        <CommunityListings />
      </Container>
      <CommunityModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        mode="create"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onSubmit={handleSubmit}
        isSubmitting={isCreating}
      />
    </Fragment>
  );
};

export { CommunityManagement };
